import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
import RoleRoute from "./components/RoleRoute";
import { Toaster } from "react-hot-toast";

const Login = lazy(() => import("./pages/login/Login"));
const HomePage = lazy(() => import("./pages/home/HomePage"));
const UsersPage = lazy(() => import("./pages/customers and users/users/UsersPage"));
const CreateUserPage = lazy(() => import("./pages/customers and users/users/CreateUserPage"));
const EditUserPage = lazy(() => import("./pages/customers and users/users/EditUserPage"));
const CustomersPage = lazy(() => import("./pages/customers and users/customers/CustomersPage"));
const RidersPage = lazy(() => import("./pages/customers and users/riders/RidersPage"));
const CreateRiderPage = lazy(() => import("./pages/customers and users/riders/CreateRiderPage"));
const EditRiderPage = lazy(() => import("./pages/customers and users/riders/EditRiderPage"));
const LocationsPage = lazy(() => import("./pages/location and settings/locations/LocationsPage"));
const CreateLocationPage = lazy(() => import("./pages/location and settings/locations/CreateLocationPage"));
const EditLocationPage = lazy(() => import("./pages/location and settings/locations/EditLocationPage"));
const DeliveryChargesPage = lazy(() => import("./pages/location and settings/settings/DeliveryChargesPage"));
const CreateDeliveryChargePage = lazy(() => import("./pages/location and settings/settings/CreateDeliveryChargePage"));
const PickupTimesPage = lazy(() => import("./pages/location and settings/settings/PickupTimesPage"));
const CreatePickupTimePage = lazy(() => import("./pages/location and settings/settings/CreatePickupTimePage"));
const DeliveryTimesPage = lazy(() => import("./pages/location and settings/settings/DeliveryTimesPage"));
const CreateDeliveryTimePage = lazy(() => import("./pages/location and settings/settings/CreateDeliveryTimePage"));
const SystemSettingsPage = lazy(() => import("./pages/location and settings/settings/SystemSettingsPage"));
const ProductsPage = lazy(() => import("./pages/products/ProductsPage"));
const CreateProductPage = lazy(() => import("./pages/products/CreateProductPage"));
const EditProductPage = lazy(() => import("./pages/products/EditProductPage"));
const CategoriesPage = lazy(() => import("./pages/categories/CategoriesPage"));
const EditCategoryPage = lazy(() => import("./pages/categories/EditCategoryPage"));
const CreateCategoryPage = lazy(() => import("./pages/categories/CreateCategoryPage"));
const EarnPointsPage = lazy(() => import("./pages/loyality points/earnPoints/EarnPointsPage"));
const CreateEarnPointPage = lazy(() => import("./pages/loyality points/earnPoints/CreateEarnPointPage"));
const EditEarnPointPage = lazy(() => import("./pages/loyality points/earnPoints/EditEarnPointPage"));
const CreateGeoTargetDiscountPage = lazy(() => import("./pages/promotions/geotargetdiscounts/CreateGeoTargetDiscountPage"));
const GeoTargetDiscountsPage = lazy(() => import("./pages/promotions/geotargetdiscounts/GeoTargetDiscountsPage"));
const CreateCrossPromotionPage = lazy(() => import("./pages/promotions/crosspromotion/CreateCrossPromotionPage"));
const CrossPromotionsPage = lazy(() => import("./pages/promotions/crosspromotion/CrossPromotionPage"));
const DiscountsPage = lazy(() => import("./pages/promotions/discounts/DiscountsPage"));
const CreateDiscountPage = lazy(() => import("./pages/promotions/discounts/CreateDiscountPage"));
const OfferPopupsPage = lazy(() => import("./pages/promotions/offerpopup/OfferPopupsPage"));
const CreateOfferPopupPage = lazy(() => import("./pages/promotions/offerpopup/CreateOfferPopupPage"));
const NewUserDiscountPage = lazy(() => import("./pages/promotions/newuserdiscounts/NewUserDiscountPage"));
const CreateNewUserDiscountPage = lazy(() => import("./pages/promotions/newuserdiscounts/CreateNewUserDiscountPage"));
const DeliveryPage = lazy(() => import("./pages/operations/orders/DeliveryPage"));
const RiderAssignPage = lazy(() => import("./pages/operations/riderAssign/RiderAssignPage"));
const OnGoingPage = lazy(() => import("./pages/operations/onGoing/OnGoingPage"));
const ZOCounterPage = lazy(() => import("./pages/operations/zocounter/ZOCounterPage"));
const BranchesPage = lazy(() => import("./pages/location and settings/branches/BranchesPage"));
const CreateBranchPage = lazy(() => import("./pages/location and settings/branches/CreateBranchPage"));
const EditBranchPage = lazy(() => import("./pages/location and settings/branches/EditBranchPage"));
const PaymentHistoryView = lazy(() => import("./pages/reports/operations/PaymentHistoryView"));
const Reports = lazy(() => import("./pages/reports/Reports"));
const PickupReportPage = lazy(() => import("./pages/reports/operations/PickupReportPage"));
const DeliveryReportPage = lazy(() => import("./pages/reports/operations/DeliveryReportPage"));
const AllOrdersReportPage = lazy(() => import("./pages/reports/operations/AllOrdersReportPage"));
const CollectionOrderPage = lazy(() => import("./pages/pos/CollectionOrderPage"));
const SoftwareManualSubMenusPage = lazy(() => import("./pages/location and settings/software manual/SoftwareManualSubMenusPage"));
const CreateSoftwareManualSubMenuPage = lazy(() => import("./pages/location and settings/software manual/CreateSoftwareManualSubMenuPage"));
const EditSoftwareManualSubMenuPage = lazy(() => import("./pages/location and settings/software manual/EditSoftwareManualSubMenuPage"));
const SoftwareManualPage = lazy(() => import("./pages/manual/SoftwareManualPage"));
const SoftwareManualDetailsPage = lazy(() => import("./pages/manual/SoftwareManualDetailsPage"));
const DiscountPromotionPerformance = lazy(() => import("./pages/reports/marketing/DiscountPromotionPerformance"));
const CancellationRefundReport = lazy(() => import("./pages/reports/operations/CancellationRefundReport"));
const CreditReceivablesReport = lazy(() => import("./pages/reports/finance/CreditReceivablesReport"));
const OrderLevelSalesSummary = lazy(() => import("./pages/reports/sales/OrderLevelSalesSummary"));
const AreaRegionSalesCustomerPerformance = lazy(() => import("./pages/reports/marketing/AreaRegionSalesCustomerPerformance"));
const ItemServicePopularity = lazy(() => import("./pages/reports/marketing/ItemServicePopularity"));
const AverageDaysBetweenOrders = lazy(() => import("./pages/reports/marketing/AverageDaysBetweenOrders"));
const AppDownloadsConversion = lazy(() => import("./pages/reports/marketing/AppDownloadsConversion"));
const DriverServiceQualityFeedback = lazy(() => import("./pages/reports/director/DriverServiceQualityFeedback"));
const MarketingNormalExpressComparison = lazy(() => import("./pages/reports/marketing/MarketingNormalExpressComparison"));
const TargetVsAchievementReport = lazy(() => import("./pages/reports/marketing/TargetVsAchievementReport"));
const CustomerSegmentationReport = lazy(() => import("./pages/reports/marketing/CustomerSegmentationReport"));
const DamageReport = lazy(() => import("./pages/reports/operations/DamageReport"));
const LostReport = lazy(() => import("./pages/reports/operations/LostReport"));
const PickupReportPageNew = lazy(() => import("./pages/reports/operations/PickupReportPageNew"));
const DriverHandoverVerificationReport = lazy(() => import("./pages/reports/finance/DriverHandoverVerificationReport"));
const MainCashierCollectionSummary = lazy(() => import("./pages/reports/finance/MainCashierCollectionSummary"));
const CardPaymentReconciliationReport = lazy(() => import("./pages/reports/finance/CardPaymentReconciliationReport"));
const FinanceDashboard = lazy(() => import("./pages/monitoring/finance/FinanceDashboard"));
const CustomerMarketingDashboard = lazy(() => import("./pages/monitoring/marketing/CustomerMarketingDashboard"));
const OperationsDashboard = lazy(() => import("./pages/monitoring/operations/OperationsDashboard"));
const PaymentGatewayOnlineSettlementReport = lazy(() => import("./pages/reports/finance/PaymentGatewayOnlineSettlementReport"));
const InvoiceStatusReport = lazy(() => import("./pages/reports/finance/InvoiceStatusReport"));
const Dashboards = lazy(() => import("./pages/monitoring/Dashboards"));
const MainMenuScreen = lazy(() => import("./pages/home/MainMenuScreen"));

function App() {
  return (
    <>
      <Toaster
        position="top-right"
        containerStyle={{
          top: 20,
          right: 20,
          zIndex: 99999,
        }}
      />
      {
        <BrowserRouter>
          <Suspense fallback={<div className="flex min-h-screen items-center justify-center text-sm text-gray-600">Loading page...</div>}>
            <Routes>
            <Route path="/branches" element={<BranchesPage />} />
            <Route path="/branches/create" element={<CreateBranchPage />} />
            <Route path="/branches/edit/:id" element={<EditBranchPage />} />

            <Route
              path="/"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />

            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/main-menu"
              element={
                <ProtectedRoute>
                  <MainMenuScreen />
                </ProtectedRoute>
              }
            />

            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "SuperAdmin"]}>
                    <UsersPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/users/create"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "SuperAdmin"]}>
                    <CreateUserPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/users/edit/:id"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "SuperAdmin"]}>
                    <EditUserPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/riders/edit/:id"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "SuperAdmin"]}>
                    <EditRiderPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/orders/ongoing"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Admin Team", "Staff"]}>
                    <OnGoingPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/orders/zocounter"
              element={
                <ProtectedRoute>
                  <RoleRoute
                    allowedRoles={[
                      "Admin",
                      "Staff",
                      "ZO Counter",
                      "Admin Team",
                    ]}
                  >
                    <ZOCounterPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/orders/riderassign"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Admin Team", "Staff"]}>
                    <RiderAssignPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/orders/deliveryonly"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Admin Team", "Staff"]}>
                    <DeliveryPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/ecommerceproducts"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <div>Ecommerce Products</div>
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/customers"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <CustomersPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/riders"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <RidersPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/riders/create"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <CreateRiderPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/category/categories"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <div>Categories Page</div>
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/locations"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <LocationsPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/locations/create"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Super Admin"]}>
                    <CreateLocationPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/locations/edit/:id"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Super Admin"]}>
                    <EditLocationPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/categories"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <CategoriesPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/categories/edit/:id"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Super Admin", "Staff"]}>
                    <EditCategoryPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/earnpointsperorder"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Super Admin", "Staff"]}>
                    <EarnPointsPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/earnpointsperorder/create"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Super Admin", "Staff"]}>
                    <CreateEarnPointPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/earnpointsperorder/edit/:id"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Super Admin"]}>
                    <EditEarnPointPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/cross-promotions"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Super Admin", "Staff"]}>
                    <CrossPromotionsPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/cross-promotions/create"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Super Admin", "Staff"]}>
                    <CreateCrossPromotionPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/new-user-discounts"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Super Admin", "Staff"]}>
                    <NewUserDiscountPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/new-user-discounts/create"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Super Admin", "Staff"]}>
                    <CreateNewUserDiscountPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/offer-popups"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Super Admin", "Staff"]}>
                    <OfferPopupsPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/offer-popups/create"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Super Admin", "Staff"]}>
                    <CreateOfferPopupPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/discounts"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Super Admin", "Staff"]}>
                    <DiscountsPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/discounts/create"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Super Admin", "Staff"]}>
                    <CreateDiscountPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/geo-discounts"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Super Admin", "Staff"]}>
                    <GeoTargetDiscountsPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/geo-discounts/create"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Super Admin", "Staff"]}>
                    <CreateGeoTargetDiscountPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            {/* <Route
          path="/geo-discounts/edit/:id"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={["Admin", "SuperAdmin"]}>
                <EditEarnPointPage/>
              </RoleRoute>
            </ProtectedRoute>
          }
        /> */}

            <Route
              path="/categories/create"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <CreateCategoryPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/products"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <ProductsPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/products/edit/:id"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin", "Super Admin"]}>
                    <EditProductPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/products/create"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <CreateProductPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <SystemSettingsPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/deliverytimes"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <DeliveryTimesPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/deliverytimes/create"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <CreateDeliveryTimePage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/pickuptimes"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <PickupTimesPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/pickuptimes/create"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <CreatePickupTimePage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/deliverycharges"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <DeliveryChargesPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/deliverycharges/create"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <CreateDeliveryChargePage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <div>Settings Page</div>
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports"
              element={
                <ProtectedRoute>
                  <Reports />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboards />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports/payment-history"
              element={
                <ProtectedRoute>
                  <RoleRoute
                    allowedRoles={["Admin", "Admin Team", "Staff", "CEO"]}
                  >
                    <PaymentHistoryView />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports/pickup-report"
              element={
                <ProtectedRoute>
                  <PickupReportPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports/delivery-report"
              element={
                <ProtectedRoute>
                  <DeliveryReportPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports/all-orders-report"
              element={
                <ProtectedRoute>
                  <AllOrdersReportPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/pos"
              element={
                <ProtectedRoute>
                  <CollectionOrderPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/software-manual/submenus"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <SoftwareManualSubMenusPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/software-manual/submenus/create"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <CreateSoftwareManualSubMenuPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/software-manual/submenus/edit/:id"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={["Admin"]}>
                    <EditSoftwareManualSubMenuPage />
                  </RoleRoute>
                </ProtectedRoute>
              }
            />
            {/* Software Manual - normal authenticated users */}
            <Route
              path="/software-manual"
              element={
                <ProtectedRoute>
                  <SoftwareManualPage />
                </ProtectedRoute>
              }
            />

            {/* Selected Manual Details */}
            <Route
              path="/software-manual/details/:id"
              element={
                <ProtectedRoute>
                  <SoftwareManualDetailsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports/marketing/discount-promotion-performance"
              element={
                <ProtectedRoute>
                  <DiscountPromotionPerformance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/operations/cancellation-refund-report"
              element={
                <ProtectedRoute>
                  <CancellationRefundReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/finance/credit-receivables-report"
              element={
                <ProtectedRoute>
                  <CreditReceivablesReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/sales/order-level-sales-summary"
              element={
                <ProtectedRoute>
                  <OrderLevelSalesSummary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/marketing/area-region-sales-customer-performance"
              element={
                <ProtectedRoute>
                  <AreaRegionSalesCustomerPerformance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/marketing/item-service-popularity"
              element={
                <ProtectedRoute>
                  <ItemServicePopularity />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/marketing/average-days-between-orders"
              element={
                <ProtectedRoute>
                  <AverageDaysBetweenOrders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/marketing/app-downloads-conversion"
              element={
                <ProtectedRoute>
                  <AppDownloadsConversion />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/director/driver-service-quality-feedback"
              element={
                <ProtectedRoute>
                  <DriverServiceQualityFeedback />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/marketing/marketing-normal-express-comparison"
              element={
                <ProtectedRoute>
                  <MarketingNormalExpressComparison />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/sales/target-vs-achievement-report"
              element={
                <ProtectedRoute>
                  <TargetVsAchievementReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/marketing/customer-segmentation-report"
              element={
                <ProtectedRoute>
                  <CustomerSegmentationReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/operations/damage-report"
              element={
                <ProtectedRoute>
                  <DamageReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/operations/lost-report"
              element={
                <ProtectedRoute>
                  <LostReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/operations/pickup-report-new"
              element={
                <ProtectedRoute>
                  <PickupReportPageNew />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/operations/driver-handover-verification-report"
              element={
                <ProtectedRoute>
                  <DriverHandoverVerificationReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/finance/main-cashier-collection-summary"
              element={
                <ProtectedRoute>
                  <MainCashierCollectionSummary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/finance/card-payment-reconciliation-report"
              element={
                <ProtectedRoute>
                  <CardPaymentReconciliationReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/finance"
              element={
                <ProtectedRoute>
                  <FinanceDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/marketing"
              element={
                <ProtectedRoute>
                  <CustomerMarketingDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/operations"
              element={
                <ProtectedRoute>
                  <OperationsDashboard />
                </ProtectedRoute>
              }
            />

             <Route
               path="/reports/finance/payment-gateway-online-settlement-report"
              element={
                <ProtectedRoute>
                  <PaymentGatewayOnlineSettlementReport />
                </ProtectedRoute>
              }
            />
             <Route
               path="/reports/finance/invoice-status-report"
              element={
                <ProtectedRoute>
                  <InvoiceStatusReport />
                </ProtectedRoute>
              }
            />
            </Routes>
          </Suspense>
        </BrowserRouter>
      }
    </>
  );
}

export default App;
